import { getAuth, onAuthStateChanged } from 'firebase/auth';

class TokenManager {
  private refreshTimer: NodeJS.Timeout | null = null;
  private tokenExpiryTime: number = 0;

  // Schedule token refresh 5 minutes before expiry
  scheduleRefresh() {
    const auth = getAuth();
    const user = auth.currentUser;
    
    if (!user) return;

    // Clear existing timer
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
    }

    // Get current token and check expiry
    user.getIdToken().then(token => {
      // Decode JWT to get expiry time
      const payload = JSON.parse(atob(token.split('.')[1]));
      const expiryTime = payload.exp * 1000; // Convert to milliseconds
      this.tokenExpiryTime = expiryTime;

      // Refresh 5 minutes before expiry
      const refreshTime = expiryTime - Date.now() - (5 * 60 * 1000);
      
      if (refreshTime > 0) {
        this.refreshTimer = setTimeout(() => {
          this.refreshToken();
        }, refreshTime);
      }
    }).catch(error => {
      console.error('Error getting token for refresh scheduling:', error);
    });
  }

  // Force refresh token
  async refreshToken(): Promise<string> {
    const auth = getAuth();
    const user = auth.currentUser;
    
    if (!user) {
      throw new Error('No authenticated user');
    }

    try {
      const newToken = await user.getIdToken(true); // Force refresh
      localStorage.setItem('authToken', newToken);
     
      // Schedule next refresh
      this.scheduleRefresh();
      
      return newToken;
    } catch (error) {
      console.error('Token refresh failed:', error);
      // Handle refresh failure (redirect to login)
      this.handleRefreshFailure();
      throw error;
    }
  }

  // Check if token is expiring soon (within 5 minutes)
  isTokenExpiringSoon(): boolean {
    const token = localStorage.getItem('authToken');
    if (!token) return false;

    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      const expiryTime = payload.exp * 1000;
      const fiveMinutesFromNow = Date.now() + (5 * 60 * 1000);
      return expiryTime <= fiveMinutesFromNow;
    } catch (error) {
      console.error('Error checking token expiry:', error);
      return true; // Assume it's expiring if we can't parse
    }
  }

  handleRefreshFailure() {
    localStorage.removeItem('authToken');
    window.location.href = '/login';
  }

  // Start monitoring auth state
  startAuthMonitoring() {
    const auth = getAuth();
    
    onAuthStateChanged(auth, (user) => {
      if (user) {
        this.scheduleRefresh();
      } else {
        if (this.refreshTimer) {
          clearTimeout(this.refreshTimer);
          this.refreshTimer = null;
        }
      }
    });
  }

  // Stop monitoring
  stopMonitoring() {
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
      this.refreshTimer = null;
    }
  }
}

export const tokenManager = new TokenManager();
